import React, { useState } from "react";
import { useForm, ValidationError } from "@formspree/react";
import { motion } from "framer-motion";
import {
  FaGuitar,
  FaMusic,
  FaStar,
  FaWhatsapp,
  FaInstagram,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import about from "../About us.jpeg";
import logo from "../gs-logo.png";

// Animation Variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } },
};

const courses = [
  {
    level: "Beginner",
    desc: "Start your guitar journey with basics and fun songs.",
    icon: <FaGuitar className="text-3xl text-blue-400" />,
  },
  {
    level: "Intermediate",
    desc: "Build on your skills with chords, strumming, and melodies.",
    icon: <FaMusic className="text-3xl text-purple-400" />,
  },
  {
    level: "Advanced",
    desc: "Master advanced techniques and perform confidently.",
    icon: <FaStar className="text-3xl text-yellow-400" />,
  },
];

const curriculum = [
  { title: "Introduction & Setup", desc: "Get to know your guitar and tuning basics." },
  { title: "Chords & Strumming", desc: "Learn essential chords and strumming patterns." },
  { title: "Songs & Rhythm", desc: "Play popular songs and improve your rhythm." },
  { title: "Performance & Improvisation", desc: "Showcase your skills and improvise." },
];

const events = [
  {
    date: "Sep 15, 2025",
    title: "Open Mic Night",
    desc: "Showcase your talent at our monthly open mic event!",
  },
  {
    date: "Oct 5, 2025",
    title: "Guitar Workshop",
    desc: "Hands-on workshop with guest artists.",
  },
];

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const handleMenuToggle = () => setMenuOpen((open) => !open);

  return (
    <div className="font-sans bg-gradient-to-b from-white via-blue-50 to-purple-50 text-gray-900 min-h-screen">
      {/* Header */}
      <header className="w-full py-4 px-4 sm:px-10 border-b border-gray-200 bg-gradient-to-r from-blue-100 to-purple-100 shadow-md sticky top-0 z-50 backdrop-blur-md">
        <div className="flex justify-between items-center relative">
          <div className="flex flex-col items-center md:items-start">
            <motion.img
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-16 w-16 md:h-20 md:w-20 object-contain"
              src={logo}
              alt="Logo"
            />
          </div>

          {/* Hamburger */}
          <button
            className="md:hidden absolute top-4 right-4 text-3xl text-blue-600 focus:outline-none"
            onClick={handleMenuToggle}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>

          {/* Nav Links */}
          <nav
            className={`flex-col md:flex md:flex-row flex-wrap justify-center gap-6 text-base font-medium 
            md:mt-0 bg-white/90 md:bg-transparent rounded-xl shadow-md md:shadow-none 
            absolute md:static top-16 left-0 w-full md:w-auto z-40 transition-all duration-300 
            ${menuOpen ? "flex animate-fadeIn" : "hidden md:flex"}`}
          >
            <a href="#courses" className="hover:text-blue-600 px-4 py-3 md:p-0 transition">
              Courses
            </a>
            <a href="#about" className="hover:text-blue-600 px-4 py-3 md:p-0 transition">
              About
            </a>
            <a href="#curriculum" className="hover:text-blue-600 px-4 py-3 md:p-0 transition">
              Curriculum
            </a>
            <a href="#events" className="hover:text-blue-600 px-4 py-3 md:p-0 transition">
              Events
            </a>
            <a href="#contact" className="hover:text-blue-600 px-4 py-3 md:p-0 transition">
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="w-full py-20 px-4 text-center bg-gradient-to-br from-blue-100 via-white to-purple-100">
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-4xl md:text-6xl font-extrabold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600"
        >
          Welcome to Guitar Shaala
        </motion.h2>
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-lg md:text-xl mb-6 text-gray-700 max-w-2xl mx-auto"
        >
          Learn, play, and perform guitar with expert instructors and a vibrant
          community.
        </motion.p>
        <motion.a
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          href="#contact"
          className="inline-block px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-full shadow-lg hover:opacity-90 transition"
        >
          Book a Free Demo
        </motion.a>
      </section>

      {/* Courses */}
      <section id="courses" className="py-16 px-4 max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl font-bold text-center mb-12"
        >
          Our Courses
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
          {courses.map((course, idx) => (
            <motion.div
              key={idx}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white/70 backdrop-blur-md rounded-2xl border border-gray-100 p-8 flex flex-col items-center shadow-md hover:shadow-xl transition"
            >
              <div className="mb-4">{course.icon}</div>
              <h3 className="text-xl font-semibold mb-2">{course.level}</h3>
              <p className="text-gray-600 text-center">{course.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 px-4 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:w-1/2 flex justify-center"
          >
            <img
              src={about}
              alt="About Guitar"
              className="w-full max-w-sm rounded-3xl shadow-lg object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="md:w-1/2"
          >
            <h2 className="text-3xl font-bold mb-4">About Guitar Shaala</h2>
            <p className="text-gray-700 mb-4">
              Guitar Shaala was founded to make music accessible to everyone.
              Our teaching style blends fun, creativity, and structured
              learning, ensuring every student finds their unique sound.
            </p>
            <p className="text-gray-700">
              Whether you’re a complete beginner or looking to refine your
              skills, our experienced instructors guide you every step of the
              way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Curriculum */}
      <section id="curriculum" className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-3xl font-extrabold text-center mb-16 text-gray-800"
          >
            Curriculum
          </motion.h2>
          <div className="relative">
            <div className="absolute top-0 left-6 md:left-1/2 transform md:-translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 rounded-full shadow-lg"></div>
            <ol className="space-y-12 relative">
              {curriculum.map((step, idx) => (
                <motion.li
                  key={idx}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={idx}
                  className={`flex flex-col md:flex-row items-center gap-6 relative ${
                    idx % 2 === 0 ? "md:flex-row-reverse md:text-right" : ""
                  }`}
                >
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center font-bold text-white shadow-lg mx-auto md:mx-0 relative">
                      {idx + 1}
                      <span className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 opacity-50 animate-ping"></span>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition w-full md:w-5/12 relative z-10">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Events */}
      <section id="events" className="py-20 px-4 max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl font-bold text-center mb-12"
        >
          Events & Workshops
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {events.map((event, idx) => (
            <motion.div
              key={idx}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-white/80 backdrop-blur-md rounded-2xl border border-gray-100 p-6 shadow-md hover:shadow-xl transition"
            >
              <span className="text-sm text-blue-600 font-semibold mb-2 block">
                {event.date}
              </span>
              <h3 className="text-lg font-bold mb-2">{event.title}</h3>
              <p className="text-gray-600 mb-4">{event.desc}</p>
              <a
                href="#contact"
                className="inline-block px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-full hover:opacity-90 transition"
              >
                Join Now
              </a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 px-6 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-3xl font-extrabold text-center mb-12 text-gray-800"
          >
            Get in Touch
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/80 backdrop-blur-md p-8 rounded-2xl shadow-lg hover:shadow-xl transition"
            >
              <ContactForm />
            </motion.div>

            {/* Social */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col items-center md:items-start text-center md:text-left space-y-8"
            >
              <p className="text-lg text-gray-700 max-w-md">
                Have questions, feedback, or just want to say hello? Drop us a
                message or connect through social media.
              </p>
              <div className="flex gap-5">
                <motion.a
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  href="https://wa.me/914442812144"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 hover:bg-green-400 text-white rounded-full p-4 text-2xl shadow-md transition"
                >
                  <FaWhatsapp />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.2, rotate: -5 }}
                  href="https://www.instagram.com/guitar_shaala/"
                  className="bg-pink-500 hover:bg-pink-400 text-white rounded-full p-4 text-2xl shadow-md transition"
                >
                  <FaInstagram />
                </motion.a>
              </div>
              <div className="text-gray-700 space-y-2">
                <p>
                  <strong>Email:</strong> theguitarshaala@gmail.com
                </p>
                <p>
                  <strong>Phone:</strong> +91 44 4281 2144
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-6 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-center text-white">
        &copy; 2025 Guitar Shaala. All rights reserved.
      </footer>
    </div>
  );
}

// Contact Form
function ContactForm() {
  const [state, handleSubmit] = useForm("xldwzjka");

  if (state.succeeded) {
    return (
      <p className="text-green-600 font-semibold">
        ✅ Thanks! Your message has been sent.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <input
        type="text"
        name="name"
        placeholder="Your Name"
        required
        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      <input
        type="email"
        name="email"
        placeholder="Your Email"
        required
        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      <ValidationError prefix="Email" field="email" errors={state.errors} />
      <textarea
        name="message"
        rows="5"
        placeholder="Your Message"
        required
        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
      ></textarea>
      <ValidationError prefix="Message" field="message" errors={state.errors} />
      <button
        type="submit"
        disabled={state.submitting}
        className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:opacity-90 transition"
      >
        {state.submitting ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
}
